(() => {
  'use strict';

  const tooltipSelector = '.oke-reviewsSummary-heading .oke-tooltip';
  const readMoreSelector = '.oke-reviewContent-readMore';
  let nextId = 0;
  let cancelPendingFocus;
  let keyboardFilter;
  const keyboardTooltips = new WeakSet();

  // This menu has no Alpine runtime on the storefront. Keep its behavior native.
  document.querySelectorAll('#mega-menu-metaobject [data-medcline-desktop-menu]').forEach((item) => {
    const trigger = item.querySelector('[data-menu-trigger]');
    const panel = item.querySelector('[data-menu-panel]');
    if (!trigger || !panel) return;
    let closeTimer;
    const close = () => {
      clearTimeout(closeTimer);
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    };
    const open = () => {
      document.querySelectorAll('#mega-menu-metaobject [data-menu-trigger][aria-expanded="true"]')
        .forEach((other) => {
          if (other !== trigger) other.closest('[data-medcline-desktop-menu]')
            .dispatchEvent(new Event('medcline:close-menu'));
        });
      clearTimeout(closeTimer);
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    };
    item.addEventListener('medcline:close-menu', close);
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (!panel.contains(document.activeElement)) close();
      }, 350);
    });
    panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    trigger.addEventListener('click', () => {
      if (trigger.tagName === 'BUTTON') panel.hidden ? open() : close();
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || (trigger.tagName === 'A' && event.key === ' ')) {
        event.preventDefault();
        open();
      }
    });
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || panel.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      close();
      trigger.focus();
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) close();
    });
  });

  const setAttribute = (element, name, value) => {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const ensureId = (element, prefix) => {
    if (!element.id) element.id = `medcline-${prefix}-${++nextId}`;
    return element.id;
  };
  const reviewExpanded = (content) => !content.matches('.oke-is-truncated') &&
    !content.querySelector('.oke-is-truncated');

  function prepare(root) {
    const elements = (selector) => [
      ...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector),
    ];
    elements(tooltipSelector).forEach((tooltip) => {
      const content = tooltip.querySelector('.oke-tooltip-content');
      if (!content) return;
      setAttribute(tooltip, 'role', 'button');
      setAttribute(tooltip, 'tabindex', '0');
      setAttribute(tooltip, 'aria-label', 'About the reviews summary');
      setAttribute(tooltip, 'aria-controls', ensureId(content, 'review-summary'));
      setAttribute(tooltip, 'aria-describedby', content.id);
      if (!tooltip.hasAttribute('aria-expanded')) setAttribute(tooltip, 'aria-expanded', 'false');
      setAttribute(content, 'role', 'tooltip');
      setAttribute(content, 'aria-hidden', tooltip.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
    });
    elements(readMoreSelector).forEach((button) => {
      const content = button.closest('.oke-reviewContent');
      const body = content?.querySelector('.oke-reviewContent-body');
      if (!body) return;
      setAttribute(button, 'aria-controls', ensureId(body, 'review-body'));
      setAttribute(button, 'aria-expanded', reviewExpanded(content) ? 'true' : 'false');
    });
  }

  function tooltipState(tooltip, open, dismissed = false) {
    setAttribute(tooltip, 'data-medcline-tooltip-open', open ? 'true' : 'false');
    setAttribute(tooltip, 'data-medcline-tooltip-dismissed', dismissed ? 'true' : 'false');
    setAttribute(tooltip, 'aria-expanded', open ? 'true' : 'false');
    const content = tooltip.querySelector('.oke-tooltip-content');
    if (content) setAttribute(content, 'aria-hidden', open ? 'false' : 'true');
  }

  // Wait for Vue's actual content update, and never steal focus after another action.
  function focusAfterUpdate(root, trigger, findUpdatedTarget) {
    cancelPendingFocus?.();
    let timer;
    let frame;
    const stop = () => {
      observer.disconnect();
      clearTimeout(timer);
      clearTimeout(frame);
      document.removeEventListener('focusin', moved, true);
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', key, true);
      if (cancelPendingFocus === stop) cancelPendingFocus = undefined;
    };
    const moved = (event) => {
      if (event.target !== trigger && event.target !== document.body) stop();
    };
    const pointer = (event) => { if (!trigger.contains(event.target)) stop(); };
    const key = (event) => { if (event.key === 'Tab' || event.key === 'Escape') stop(); };
    const check = () => {
      const target = findUpdatedTarget();
      if (!target?.isConnected) return;
      stop();
      if (!target.matches('button, a[href], input, select, textarea')) setAttribute(target, 'tabindex', '-1');
      target.focus();
    };
    const observer = new MutationObserver(() => {
      clearTimeout(frame);
      // A short timer, not rAF: animation frames never fire in a throttled or background tab.
      frame = setTimeout(check, 30);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'style', 'aria-busy'] });
    document.addEventListener('focusin', moved, true);
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', key, true);
    timer = setTimeout(stop, 5000);
    cancelPendingFocus = stop;
  }

  document.addEventListener('keydown', (event) => {
    const filter = event.target.closest?.('[data-oke-widget] .oke-w-breakdownModule-distribution-highlight');
    if (filter && (event.key === 'Enter' || event.key === ' ') && !event.repeat) {
      // Okendo's div handles keyboard activation directly, without a native click.
      keyboardFilter = filter;
      setTimeout(() => { if (keyboardFilter === filter) keyboardFilter = undefined; }, 500);
      focusFilterResults(filter);
    }
    const tooltip = event.target.closest?.(tooltipSelector);
    if (!tooltip) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      keyboardTooltips.delete(tooltip);
      tooltipState(tooltip, false, true);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (event.repeat) return;
      const open = tooltip.getAttribute('aria-expanded') !== 'true';
      open ? keyboardTooltips.add(tooltip) : keyboardTooltips.delete(tooltip);
      tooltipState(tooltip, open, !open);
    }
  }, true);

  document.addEventListener('pointerover', (event) => {
    const tooltip = event.target.closest?.(tooltipSelector);
    if (tooltip && !tooltip.contains(event.relatedTarget)) tooltipState(tooltip, true);
  }, true);
  document.addEventListener('pointerout', (event) => {
    const tooltip = event.target.closest?.(tooltipSelector);
    if (tooltip && !tooltip.contains(event.relatedTarget)) tooltipState(tooltip, keyboardTooltips.has(tooltip));
  }, true);
  document.addEventListener('focusout', (event) => {
    const tooltip = event.target.closest?.(tooltipSelector);
    if (tooltip && !tooltip.contains(event.relatedTarget)) {
      keyboardTooltips.delete(tooltip);
      tooltipState(tooltip, false, true);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(readMoreSelector);
    if (button) {
      const original = button.closest('.oke-reviewContent');
      const root = original?.closest('[data-oke-widget], .oke-reviewCarousel') || document.body;
      const body = original?.querySelector('.oke-reviewContent-body');
      if (!original || !body) return;
      const title = original.querySelector('.oke-reviewContent-title')?.textContent.trim();
      const bodyStart = body.textContent.trim().slice(0, 80);
      const wasExpanded = reviewExpanded(original);
      focusAfterUpdate(root, button, () => {
        const live = original.isConnected ? original : [...root.querySelectorAll('.oke-reviewContent')]
          .find((item) => !item.closest('.hooper-slide:not(.is-active), [aria-hidden="true"]') &&
            item.querySelector('.oke-reviewContent-title')?.textContent.trim() === title &&
            item.querySelector('.oke-reviewContent-body')?.textContent.trim().startsWith(bodyStart));
        if (!live || reviewExpanded(live) === wasExpanded) return null;
        prepare(live);
        return wasExpanded ? live.querySelector(readMoreSelector) : live.querySelector('.oke-reviewContent-body');
      });
      return;
    }

    const filter = event.target.closest?.('[data-oke-widget] .oke-w-breakdownModule-distribution-highlight');
    if (filter && filter !== keyboardFilter) focusFilterResults(filter);
  }, true);

  function focusFilterResults(filter) {
    const widget = filter.closest('[data-oke-widget]');
    if (!widget) return;
    const oldList = widget.querySelector('.oke-w-reviews-list');
    const oldText = oldList?.textContent.trim();
    focusAfterUpdate(widget, filter, () => {
      const list = widget.querySelector('.oke-w-reviews-list');
      if (!list || !list.children.length || (list === oldList && list.textContent.trim() === oldText)) return null;
      if (widget.querySelector('[aria-busy="true"]')) return null;
      return list.querySelector('.oke-reviewContent-title') || list;
    });
  }

  const style = document.createElement('style');
  style.textContent = `
    ${tooltipSelector}[data-medcline-tooltip-open="true"] .oke-tooltip-content {
      opacity: 1 !important; visibility: visible !important;
    }
    ${tooltipSelector}[data-medcline-tooltip-dismissed="true"] .oke-tooltip-content {
      opacity: 0 !important; visibility: hidden !important;
    }
    ${tooltipSelector}:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }
    .okeReviews .oke-avatar-placeholder {
      --oke-avatar-backgroundColor: #b04a28;
    }
    .okeReviews .oke-w-reviewer-verified {
      --oke-highlightColor: #b04a28;
    }
    .okeReviews .oke-reviewsKeywords-list-category {
      --oke-filter-textColor: #b04a28;
      --oke-filter-borderColor: #b04a28;
      --oke-filter-backgroundColorActive: #b04a28;
      --oke-filter-borderColorActive: #b04a28;
    }
    .okeReviews #filter-toggle,
    .okeReviews .oke-w-writeReview,
    .okeReviews .oke-showMore-button {
      --oke-button-backgroundColor: #b04a28;
      --oke-button-borderColor: #b04a28;
    }
    /* Enlarge the grid tracks so rating filters never overlap adjacent rows. */
    .okeReviews .oke-w-breakdownModule-distribution {
      grid-auto-rows: minmax(32px, auto);
    }
    .okeReviews .oke-reviewContent-readMore {
      min-height: 32px !important;
    }
    .okeReviews .oke-reviewContent-readMore,
    .okeReviews .oke-reviewContent-body {
      scroll-margin-top: calc(var(--header-height, 100px) + 60px);
    }
  `;
  document.head.appendChild(style);
  prepare(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) prepare(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });
})();
